import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const authService = {
  async register(email: string, password: string) {
    const response = await api.post('/auth/register', { email, password });
    return response.data;
  },

  async login(email: string, password: string) {
    let recaptchaToken = '';
    try {
      const grecaptcha = (window as any).grecaptcha;
      if (grecaptcha) {
        recaptchaToken = await new Promise<string>((resolve) => {
          grecaptcha.ready(() => {
            grecaptcha.execute('6LdjOpwsAAAAAGK4eK2Ndciml4ubeqRA5Icd0BTV', { action: 'login' })
              .then(resolve).catch(() => resolve(''));
          });
        });
      }
    } catch {}
    const response = await api.post('/auth/login', { email, password, recaptchaToken });
    return response.data;
  },

  async verifyEmail(code: string, token: string) {
    const response = await api.post(
      '/auth/verify-email',
      { code },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  async resendVerificationCode(token: string) {
    const response = await api.post(
      '/auth/resend-verification',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  async getCurrentUser(token: string) {
    const response = await api.get('/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  async updateProfile(updates: any, token: string) {
    const response = await api.put('/auth/profile', updates, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  async changePassword(currentPassword: string, newPassword: string, token: string) {
    const response = await api.post(
      '/auth/change-password',
      { currentPassword, newPassword },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  async requestPasswordReset(email: string) {
    const response = await api.post('/auth/reset-password-request', { email });
    return response.data;
  },

  async verify2FA(code: string, preAuthToken: string) {
    const response = await api.post('/auth/verify-2fa', { code, preAuthToken });
    return response.data;
  },

  async resetPassword(email: string, code: string, newPassword: string) {
    const response = await api.post('/auth/reset-password', {
      email,
      code,
      newPassword,
    });
    return response.data;
  },

  async sendPhoneOtp(phone: string, token: string) {
    const response = await api.post(
      '/auth/phone/send-otp',
      { phone },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  async verifyPhoneOtp(code: string, token: string) {
    const response = await api.post(
      '/auth/phone/verify',
      { code },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  async saveTastyTradeCredentials(refreshToken: string, clientSecret: string, token: string, accountNumber?: string) {
    const body: any = { refreshToken, clientSecret };
    if (accountNumber) body.accountNumber = accountNumber;
    const response = await api.post(
      '/user/tastytrade/credentials',
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  async getTastyTradeAccount(token: string) {
    const response = await api.get('/user/tastytrade/account', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  async deleteTastyTradeCredentials(token: string, opts?: { connectionId?: string; force?: boolean }) {
    const params = new URLSearchParams();
    if (opts?.connectionId) params.append('connectionId', opts.connectionId);
    if (opts?.force) params.append('force', 'true');
    const qs = params.toString();
    const response = await api.delete(`/user/tastytrade/credentials${qs ? '?' + qs : ''}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  async cancelSubscription(token: string) {
    const response = await api.post(
      '/subscription/cancel',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  async updateSubscriptions(
    hourlyEmails: boolean,
    dailyReport: boolean,
    stockDailyReport: boolean,
    disclaimerAccepted: boolean,
    token: string
  ) {
    const response = await api.put(
      '/auth/profile',
      {
        hourlyEmailsEnabled: hourlyEmails,
        dailyReportEnabled: dailyReport,
        stockDailyReportEnabled: stockDailyReport,
        disclaimerAccepted,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },
};